import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  IBlock,
  ICompositeTextWidget,
  IGifBlock,
  IImageBlock,
  IListBlock,
  IParagraphWidget,
  ISection,
  ISeparatorBlock,
  ISummaryBlock,
  ITableBlock,
  ITitleBlock,
  IVideoBlock,
} from '../../components/widgets/types';
import { useDossier } from '../DossierContext';

/**
 * Gestion CRUD des blocs d'un dossier.
 *
 * Consomme le `DossierContext` — pas de props. Tous les indices
 * sont relatifs au `currentChapterIndex` du context, sauf
 * `changeBlock` qui prend un `chapterIndex` explicite (utilisé par
 * le drag-and-drop inter-chapitres).
 */

function createBlockByType(widgetType: string): IBlock {
  const newId = uuidv4();

  switch (widgetType) {
    case 'CompositeTextWidget':
      return {
        _id: newId,
        id: newId,
        type: 'compositeText',
        content: {
          blocks: [{ id: newId, _id: newId, rawText: '', entities: [] }],
        },
      } as ICompositeTextWidget;
    case 'paragraph':
      return {
        _id: newId,
        id: newId,
        type: 'paragraph',
        content: {
          blocks: [{ id: newId, _id: newId, rawText: '', entities: [] }],
        },
      } as IParagraphWidget;
    case 'title':
      return {
        _id: newId,
        id: newId,
        type: 'title',
        level: 1,
        content: 'Nouveau Titre',
      } as ITitleBlock;
    case 'list':
      return {
        _id: newId,
        id: newId,
        type: 'list',
        ordered: false,
        items: [{ id: uuidv4(), content: '', children: undefined }],
      } as IListBlock;
    case 'image':
      return {
        _id: newId,
        id: newId,
        type: 'image',
        imageId: '',
        src: '',
        description: '',
        alt: '',
      } as IImageBlock;
    case 'gif':
      return {
        _id: newId,
        id: newId,
        type: 'gif',
        src: '',
        description: '',
        alt: '',
      } as IGifBlock;
    case 'video':
      return {
        _id: newId,
        id: newId,
        type: 'video',
        provider: null,
        url: '',
        thumbnailUrl: '',
      } as IVideoBlock;
    case 'table':
      return {
        _id: newId,
        id: newId,
        type: 'table',
        columns: 2,
        hasHeader: true,
        rows: [
          {
            id: uuidv4(),
            cells: [
              { id: uuidv4(), content: '' },
              { id: uuidv4(), content: '' },
            ],
          },
          {
            id: uuidv4(),
            cells: [
              { id: uuidv4(), content: '' },
              { id: uuidv4(), content: '' },
            ],
          },
        ],
      } as ITableBlock;
    case 'summary':
      return { _id: newId, id: newId, type: 'summary', scope: 'dossier' } as ISummaryBlock;
    case 'separator':
      return { _id: newId, id: newId, type: 'separator' } as ISeparatorBlock;
    default:
      return { _id: newId, id: newId, type: widgetType } as IBlock;
  }
}

export function useBlocksManagement() {
  const { dossier, updateDossier, currentChapterIndex } = useDossier();

  const changeBlockLocal = useCallback(
    (sectionIndex: number, blockIndex: number, newBlock: IBlock | ICompositeTextWidget, blockIdHint?: string) => {
      updateDossier(draft => {
        const chapter = draft.content[currentChapterIndex] || draft.content[0];
        if (!chapter?.sections) return;
        const expected = chapter.sections[sectionIndex]?.blocks?.[blockIndex];
        if (blockIdHint && expected?._id !== blockIdHint) {
          for (let sIdx = 0; sIdx < chapter.sections.length; sIdx++) {
            const blocks = chapter.sections[sIdx]?.blocks ?? [];
            const bIdx = blocks.findIndex((b: any) => b._id === blockIdHint);
            if (bIdx >= 0) {
              blocks[bIdx] = newBlock;
              return;
            }
          }
          return;
        }
        if (!expected) return;
        chapter.sections[sectionIndex].blocks[blockIndex] = newBlock;
      });
    },
    [updateDossier, currentChapterIndex]
  );

  const changeBlock = useCallback(
    (chapterIndex: number, sectionIndex: number, blockIndex: number, newBlock: IBlock) => {
      updateDossier(draft => {
        if (!draft.content?.[chapterIndex]?.sections?.[sectionIndex]?.blocks?.[blockIndex]) {
          console.error('changeBlock: index hors limites', {
            chapterIndex,
            sectionIndex,
            blockIndex,
          });
          return;
        }
        if (newBlock.type === 'compositeText') {
          draft.content[chapterIndex].sections[sectionIndex].blocks[blockIndex] = {
            ...newBlock,
            content: {
              blocks: [(newBlock as ICompositeTextWidget).content.blocks[0]],
            },
          } as ICompositeTextWidget;
        } else {
          draft.content[chapterIndex].sections[sectionIndex].blocks[blockIndex] = newBlock;
        }
      });
    },
    [updateDossier]
  );

  const duplicateBlock = useCallback(
    (sectionIndex: number, blockIndex: number, blockIdHint?: string) => {
      updateDossier(draft => {
        const chapter = draft.content[currentChapterIndex];
        if (!chapter?.sections) return;
        let targetSectionIndex = sectionIndex;
        let targetBlockIndex = blockIndex;
        const expected = chapter.sections[sectionIndex]?.blocks?.[blockIndex];
        if (blockIdHint && expected?._id !== blockIdHint) {
          let found = false;
          for (let sIdx = 0; sIdx < chapter.sections.length; sIdx++) {
            const blocks = chapter.sections[sIdx]?.blocks ?? [];
            const bIdx = blocks.findIndex((b: any) => b._id === blockIdHint);
            if (bIdx >= 0) {
              targetSectionIndex = sIdx;
              targetBlockIndex = bIdx;
              found = true;
              break;
            }
          }
          if (!found) return;
        } else if (!expected) {
          return;
        }

        const original = chapter.sections[targetSectionIndex].blocks[targetBlockIndex];
        const clone = { ...original } as IBlock;
        clone._id = uuidv4();
        (clone as any).id = uuidv4();

        if (clone.type === 'compositeText') {
          const composite = clone as ICompositeTextWidget;
          composite.content.blocks = composite.content.blocks.map(textBlock => ({
            ...textBlock,
            _id: uuidv4(),
          }));
        }

        if (clone.type === 'list') {
          const list = clone as IListBlock;
          const dup = (items: any[]): any[] =>
            items.map(item => ({
              ...item,
              id: uuidv4(),
              children: item.children ? dup(item.children) : undefined,
            }));
          list.items = dup(list.items);
        }

        chapter.sections[targetSectionIndex].blocks.splice(targetBlockIndex + 1, 0, clone);
      });
    },
    [updateDossier, currentChapterIndex]
  );

  const deleteBlock = useCallback(
    (sectionIndex: number, blockIndex: number, blockIdHint?: string) => {
      updateDossier(draft => {
        const chapter = draft.content[currentChapterIndex];
        if (!chapter?.sections) return;
        const expected = chapter.sections[sectionIndex]?.blocks?.[blockIndex];
        if (blockIdHint && expected?._id !== blockIdHint) {
          for (let sIdx = 0; sIdx < chapter.sections.length; sIdx++) {
            const blocks = chapter.sections[sIdx]?.blocks ?? [];
            const bIdx = blocks.findIndex((b: any) => b._id === blockIdHint);
            if (bIdx >= 0) {
              blocks.splice(bIdx, 1);
              return;
            }
          }
          return;
        }
        if (!expected) return;
        chapter.sections[sectionIndex].blocks.splice(blockIndex, 1);
      });
    },
    [updateDossier, currentChapterIndex]
  );

  const addWidget = useCallback(
    (widgetType: string): string | undefined => {
      const newBlock = createBlockByType(widgetType);
      updateDossier(draft => {
        const chapter = draft.content[currentChapterIndex];
        if (!chapter) return;

        if (chapter.sections.length === 0) {
          chapter.sections.push({
            _id: uuidv4(),
            type: 'text',
            blocks: [newBlock],
          } as ISection);
        } else {
          chapter.sections[0].blocks.push(newBlock);
        }
      });
      return newBlock._id;
    },
    [updateDossier, currentChapterIndex]
  );

  const addWidgetAfter = useCallback(
    (sectionIndex: number, blockIndex: number, widgetType: string, blockIdHint?: string): string | undefined => {
      const newBlock = createBlockByType(widgetType);
      updateDossier(draft => {
        const chapter = draft.content[currentChapterIndex];
        if (!chapter?.sections) return;
        let targetSectionIndex = sectionIndex;
        let targetBlockIndex = blockIndex;
        const expected = chapter.sections[sectionIndex]?.blocks?.[blockIndex];
        if (blockIdHint && expected?._id !== blockIdHint) {
          for (let sIdx = 0; sIdx < chapter.sections.length; sIdx++) {
            const blocks = chapter.sections[sIdx]?.blocks ?? [];
            const bIdx = blocks.findIndex((b: any) => b._id === blockIdHint);
            if (bIdx >= 0) {
              targetSectionIndex = sIdx;
              targetBlockIndex = bIdx;
              break;
            }
          }
        }
        if (!chapter.sections[targetSectionIndex]?.blocks) return;
        chapter.sections[targetSectionIndex].blocks.splice(targetBlockIndex + 1, 0, newBlock);
      });
      return newBlock._id;
    },
    [updateDossier, currentChapterIndex]
  );

  return {
    dossier,
    currentChapterIndex,
    changeBlockLocal,
    changeBlock,
    duplicateBlock,
    deleteBlock,
    addWidget,
    addWidgetAfter,
  };
}

export default useBlocksManagement;
